import { useTranslation } from 'react-i18next';

import { Camera, User } from 'lucide-react';

import { useProfileImage } from './useProfileImage';

/**
 * ProfilePhotoField — 프로필 화면 맨 위의 아바타.
 *
 * 사진 선택은 네이티브만 할 수 있어서, 브라우저에서 열면 **아바타만 보이고
 * 버튼이 없다**. 눌러도 안 되는 버튼을 두는 것보다 낫다.
 *
 * **버튼은 하나다.** 카메라·앨범을 화면에 나란히 두면 사진 하나 바꾸는 데 두 번을
 * 눌러야 한다. 어디서 가져올지는 OS 가 시트로 묻는다(`pickImage` 를 `source` 없이
 * 부른다) — 사용자가 이미 다른 앱에서 매일 보는 질문이고, 취소도 그 시트가 받는다.
 * 지우기는 사진이 있을 때만 아래에 글자로 선다. 버튼으로 만들면 파괴적인 선택이
 * 사진을 바꾸는 선택과 같은 무게로 보인다.
 *
 * 저장 버튼과 무관하게 **즉시 반영된다.** 화면 아래 설문 답들은 "저장"을 눌러야
 * 남지만 사진은 고르는 즉시 저장된다 — 고른 사진이 화면에 이미 보이는데 저장을
 * 또 눌러야 한다면, 안 누르고 나간 사람은 사진이 사라진 이유를 알 수 없다.
 */

const actionClass =
  'flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-50';

export const ProfilePhotoField = () => {
  const { t } = useTranslation();
  const { image, canPick, picking, notice, pick, remove, dismissNotice } =
    useProfileImage();

  return (
    <section className="flex flex-col items-center gap-3">
      <div
        className="flex size-24 items-center justify-center overflow-hidden rounded-full border border-border bg-muted"
        // 사진이 없을 때도 자리를 차지한다 — 사진을 넣으면 화면이 밀리는 걸 막는다.
      >
        {image ? (
          <img
            src={image}
            alt={t('web.profile.photo.alt')}
            className="size-full object-cover"
          />
        ) : (
          <User
            className="size-10 text-muted-foreground"
            aria-label={t('web.profile.photo.empty')}
          />
        )}
      </div>

      {canPick && (
        <button
          type="button"
          className={actionClass}
          onClick={() => void pick()}
          disabled={picking}
        >
          <Camera className="size-4" />
          {image ? t('web.profile.photo.change') : t('web.profile.photo.add')}
        </button>
      )}

      {canPick && image && (
        <button
          type="button"
          onClick={remove}
          disabled={picking}
          className="text-xs text-muted-foreground underline underline-offset-2"
        >
          {t('web.profile.photo.remove')}
        </button>
      )}

      {/* 사진이 서버로 가지 않는다는 사실은 기기를 바꿀 때 드러난다 — 미리 말해 둔다. */}
      {canPick && (
        <p className="text-xs text-muted-foreground">
          {t('web.profile.photo.localOnly')}
        </p>
      )}

      {notice && (
        <button
          type="button"
          onClick={dismissNotice}
          className="text-xs text-destructive underline underline-offset-2"
        >
          {t(`web.profile.photo.${notice}`)}
        </button>
      )}
    </section>
  );
};
